import React from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Clock, LogOut, ArrowLeft, Moon, Sun, Home, User, MapPinned, BarChart3, FileText, FileSpreadsheet, Shield } from 'lucide-react';
import { getTheme } from '../theme';

const defaultNavItems = [
  { id: 'scans', label: 'Suivis', icon: Clock },
  { id: 'visite-terrain', label: 'Visite terrain', icon: MapPinned },
  { id: 'statistiques', label: 'Statistiques', icon: BarChart3 },
  { id: 'compte-rendu', label: 'Compte rendu', icon: FileText },
  { id: 'devis', label: 'Devis', icon: FileSpreadsheet },
  { id: 'permissions', label: 'Permissions', icon: Shield },
];

const AdminSidebar = ({
  activeTab,
  setActiveTab,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  currentUser,
  handleLogout,
  navigate,
  isDarkMode,
  toggleDarkMode,
  isMobile,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  navigationItems = defaultNavItems,
}) => {
  const theme = getTheme(isDarkMode);

  const sidebarStyle = {
    backgroundColor: theme.surface,
    borderColor: theme.border,
    color: theme.text
  };

  return (
    <div 
      className={`
        ${isMobile ? 'fixed' : 'sticky'} top-0 left-0 h-screen border-r z-50 transition-all duration-300 flex flex-col
        ${isMobile ? 
          `${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} w-64` : 
          `${isSidebarCollapsed ? 'w-20' : 'w-64'}`
        }
      `}
      style={sidebarStyle}
    >
      
      {/* Zone A - Logo/Identité Modernisée */}
      <div 
        className={`border-b flex items-center ${isSidebarCollapsed && !isMobile ? 'p-2 justify-center' : 'p-3 sm:p-4'}`}
        style={{ borderColor: theme.border }}
      >
        {!isSidebarCollapsed || isMobile ? (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-white p-1 shadow-md flex-shrink-0 border border-gray-200">
              <img 
                src="/horizon-logo.png" 
                alt="Horizon Solution" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="w-full h-full rounded-md bg-blue-600 items-center justify-center text-white font-bold text-[10px] hidden"
              >
                H
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-bold text-sm sm:text-[0.95rem] leading-tight tracking-tight truncate" style={{ color: theme.text }}>
                Horizon Solution
              </h1>
              <p className="text-[11px] sm:text-xs leading-snug mt-0.5 truncate" style={{ color: theme.textSecondary }}>
                Suivi de prestation
              </p>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-lg bg-white p-1 shadow-md border border-gray-200">
            <img 
              src="/horizon-logo.png" 
              alt="Horizon Solution" 
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div 
              className="w-full h-full rounded bg-blue-600 items-center justify-center text-white font-bold text-[10px] hidden"
            >
              H
            </div>
          </div>
        )}
      </div>

      {/* Zone B - Menu Utilisateur avec DropdownMenu */}
      <div className={`border-b ${isSidebarCollapsed && !isMobile ? 'p-2' : 'p-4'}`} style={{ borderColor: theme.border }}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className={`w-full flex items-center gap-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isSidebarCollapsed && !isMobile ? 'p-2 justify-center' : 'p-3'}`}
              style={{ 
                backgroundColor: 'transparent',
              }}
            >
              {/* Photo utilisateur avec indicateur en ligne */}
              <div className="relative flex-shrink-0">
                {currentUser?.photo ? (
                  <img 
                    src={currentUser.photo}
                    alt={currentUser.display_name} 
                    className={`rounded-full object-cover border-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} ${isSidebarCollapsed && !isMobile ? 'w-10 h-10' : 'w-11 h-11'}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`rounded-full bg-[#f76f4b] items-center justify-center ${isSidebarCollapsed && !isMobile ? 'w-10 h-10' : 'w-11 h-11'}`}
                  style={{ display: currentUser?.photo ? 'none' : 'flex' }}
                >
                  <User size={20} className="text-white" />
                </div>
                {/* Pastille verte "en ligne" */}
                <div className={`absolute bottom-0 right-0 bg-green-500 rounded-full ${isDarkMode ? 'border-gray-800' : 'border-white'} border-2 ${isSidebarCollapsed && !isMobile ? 'w-2.5 h-2.5' : 'w-3 h-3'}`}></div>
              </div>
              
              {/* Nom et position */}
              {(!isSidebarCollapsed || isMobile) && (
                <div className="flex-1 text-left overflow-hidden">
                  <p 
                    className="font-semibold text-sm truncate"
                    style={{ color: isDarkMode ? '#ffffff' : '#080808' }}
                  >
                    {currentUser?.display_name || 'Administrateur'}
                  </p>
                  <p 
                    className="text-xs truncate"
                    style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}
                  >
                    {currentUser?.position || 'Utilisateur'}
                  </p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          
          <DropdownMenuContent 
            align="start" 
            className={`w-72 p-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            sideOffset={5}
          >
            {/* ========================================= */}
            {/* === 1. TOGGLE MODE SOMBRE/CLAIR ======== */}
            {/* ========================================= */}
            <div 
              className={`mb-2 p-3 rounded-lg cursor-pointer ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDarkMode();
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isDarkMode ? (
                    <Sun size={18} className="text-yellow-500" />
                  ) : (
                    <Moon size={18} className="text-blue-400" />
                  )}
                  <span 
                    className="text-sm font-medium whitespace-nowrap"
                    style={{ color: isDarkMode ? '#ffffff' : '#080808' }}
                  >
                    {!isDarkMode ? 'Mode clair' : 'Mode sombre'}
                  </span>
                </div>
                {/* Toggle Switch */}
                <div
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                    isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      isDarkMode ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
            </div>
            
            <DropdownMenuSeparator className={isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} />
            
            {/* ========================================= */}
            {/* === 2. BOUTONS EXISTANTS =============== */}
            {/* ========================================= */}
            {/* Aucun bouton supplémentaire pour l'instant */}
            
            {/* ========================================= */}
            {/* === 3. DÉCONNEXION (TOUJOURS EN DERNIER) */}
            {/* ========================================= */}
            <DropdownMenuItem 
              onClick={handleLogout}
              className={`cursor-pointer rounded-lg mt-2 ${isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'}`}
            >
              <LogOut size={16} className="mr-2" />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Zone C - Navigation Principale Modernisée */}
      <div className="flex-1 py-5 overflow-y-auto">
        <nav className={`space-y-1.5 ${isSidebarCollapsed && !isMobile ? 'px-2' : 'px-3.5'}`}>
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            return (
              <Button
                key={item.id}
                variant="ghost"
                className={`
                  w-full transition-all duration-200 rounded-xl font-medium h-10
                  ${isActive ? 'shadow-sm' : 'hover:shadow-sm'}
                  ${isSidebarCollapsed && !isMobile ? 'p-0 justify-center' : 'px-3.5 justify-start'}
                `}
                style={{
                  backgroundColor: isActive ? theme.accent : 'transparent',
                  color: isActive ? 'white' : theme.text,
                }}
                onClick={() => {
                  setActiveTab(item.id);
                  if (isMobile) setIsMobileSidebarOpen(false);
                }}
              >
                <Icon className={`flex-shrink-0 ${isSidebarCollapsed && !isMobile ? 'h-[17px] w-[17px]' : 'h-5 w-5'}`} />
                {(!isSidebarCollapsed || isMobile) && (
                  <span className="ml-3 text-left min-w-0 flex-1 font-semibold truncate text-[0.9375rem] leading-snug">
                    {item.label}
                  </span>
                )}
              </Button>
            );
          })}
        </nav>
      </div>

      {/* Zone D - Pied de Sidebar Modernisé */}
      <div className={`border-t ${isSidebarCollapsed && !isMobile ? 'p-2 space-y-2' : 'p-4 space-y-3'}`} style={{ borderColor: theme.border }}>
        <Button
          variant="outline"
          className={`w-full rounded-xl font-medium text-[0.9375rem] transition-colors ${isSidebarCollapsed && !isMobile ? 'h-9 p-0 justify-center' : 'h-10 justify-start px-3.5'}`}
          style={{ 
            borderColor: theme.border,
            color: theme.textSecondary,
            backgroundColor: 'transparent'
          }}
          onClick={() => navigate('/')}
        >
          <Home className={`flex-shrink-0 ${isSidebarCollapsed && !isMobile ? 'h-4 w-4' : 'h-[18px] w-[18px]'}`} />
          {(!isSidebarCollapsed || isMobile) && (
            <span className="ml-2.5">Retour à l'accueil</span>
          )}
        </Button>
        
        {/* Bouton réduire/étendre sidebar */}
        {!isMobile && (
          <Button
            variant="ghost"
            className={`w-full rounded-xl font-medium text-[0.9375rem] transition-colors ${isSidebarCollapsed ? 'h-9 p-0 justify-center' : 'h-10 justify-start px-3.5'}`}
            style={{ color: theme.textSecondary }}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            <ArrowLeft className={`h-4 w-4 flex-shrink-0 transition-transform ${isSidebarCollapsed ? 'rotate-180' : ''}`} />
            {!isSidebarCollapsed && (
              <span className="ml-2.5">Réduire</span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AdminSidebar;
